// The public screen a person's link leads to (INC-07c-1, D-258): outside every
// session and outside the Gate, branded by bootstrap from the host like the
// access screen, and built from the same pieces.
//
// The token is read from the fragment of the address, which no browser sends
// to any server. The person chooses a password under the policy in force,
// repeats it, and enters with the role they have: the Gate sends them where it
// sends everybody of that role. A link refused says why and asks for the
// access to be sent again; nothing on the device is touched by the refusal.
//
// INC-12c-2 (D-291): the link of a person who forgot their password opens this
// same screen at an address of its own. Refused, whatever the reason, it says
// the link no longer serves and offers to ask for another.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { CODES, INVITATION_REFUSALS, isApiError, isNetworkError } from '../api/errors';
import { brandOf } from '../app/brand';
import { proposeDeviceLabel } from '../app/deviceLabel';
import { useRuntime, useSession } from '../app/hooks';
import { useTitleSection } from '../app/documentTitle';
import { logUnexpected } from '../app/unexpected';
import { BrandLockup } from '../ui/BrandLockup';

// The policy's minimum, as the password screen states it.
const MIN_LENGTH = 12;

/** Why the link does not lead anywhere, as a key of the catalog. */
type Refusal =
  | 'missing'
  | typeof CODES.invitationUnknown
  | typeof CODES.invitationExpired
  | typeof CODES.invitationConsumed;

function tokenFromFragment(hash: string): string {
  return decodeURIComponent(hash.replace(/^#/, '')).trim();
}

/** Whose link this is: an invitation's, or a recovery's (INC-12c-2). */
export type LinkPurpose = 'invitation' | 'recovery';

export function InvitationScreen({ purpose = 'invitation' }: { purpose?: LinkPurpose }) {
  const { t } = useTranslation();
  // INC-14a-3a-1: the tab names this screen before the organization, and no product.
  useTitleSection(t('invitation.title'), 'outside');
  const { session, engine } = useRuntime();
  const snapshot = useSession();
  const navigate = useNavigate();
  const [token] = useState(() => tokenFromFragment(window.location.hash));
  const [password, setPassword] = useState('');
  const [repeated, setRepeated] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(token === '' ? 'missing' : null);

  const organization = snapshot.bootstrap?.organization ?? null;
  const brand = brandOf(snapshot);

  // INC-15b-3 (C, point 180): what nobody expected is said in words, and its
  // code goes to the log, never to the screen.
  const unexpected = (failure: unknown): void => {
    logUnexpected('choose password', failure);
    setError(t('password.unexpectedError'));
  };

  const submit = async (event: { preventDefault(): void }): Promise<void> => {
    event.preventDefault();
    setError(null);
    if (password !== repeated) {
      setError(t('invitation.mismatch'));
      return;
    }
    setBusy(true);
    try {
      const label = snapshot.deviceLabel ?? proposeDeviceLabel(navigator.userAgent, t);
      await session.acceptInvitation(token, password, label);
      engine.trigger('manual');
      // The Gate sends the person where their role goes.
      void navigate('/', { replace: true });
    } catch (failure) {
      if (isNetworkError(failure)) setError(t('access.networkError'));
      else if (isApiError(failure)) {
        if (INVITATION_REFUSALS.has(failure.code)) setRefusal(failure.code as Refusal);
        else if (failure.code === CODES.passwordRejected) {
          setError(t('invitation.passwordRejected'));
        } else if (failure.code === CODES.rateLimited) {
          setError(
            t('access.rateLimited', { seconds: Math.ceil((failure.retryAfterMs ?? 0) / 1000) }),
          );
        } else if (failure.code === CODES.organizationSuspended) {
          setError(t('access.organizationSuspended'));
        } else unexpected(failure);
      } else unexpected(failure);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="access" data-testid="invitation-screen" data-purpose={purpose}>
      <BrandLockup
        name={organization !== null ? organization.name : t('access.neutralTitle')}
        logoUrl={brand.logoUrl}
        markUrl={brand.markUrl}
      />
      {refusal !== null && purpose === 'recovery' ? (
        <div className="card stack" data-testid="invitation-refused" data-reason={refusal}>
          <div className="alert warn" role="alert">
            {t('recovery.refused')}
          </div>
          <Link className="btn secondary block" to="/recuperar">
            {t('access.forgot')}
          </Link>
        </div>
      ) : refusal !== null ? (
        <div className="card stack" data-testid="invitation-refused" data-reason={refusal}>
          <div className="alert warn" role="alert">
            {t(`invitation.refused.${refusal}`)}
          </div>
          <p>{t('invitation.askResend')}</p>
          <Link className="btn secondary block" to="/login">
            {t('invitation.goToAccess')}
          </Link>
        </div>
      ) : (
        <form
          className="card stack"
          onSubmit={(event) => void submit(event)}
          data-testid="invitation-form"
        >
          <h2 style={{ margin: 0 }}>{t('invitation.title')}</h2>
          <p className="muted">{t('invitation.explanation')}</p>
          <div className="field">
            <label htmlFor="new-password">{t('invitation.password')}</label>
            <input
              id="new-password"
              className="input"
              type="password"
              autoComplete="new-password"
              value={password}
              required
              minLength={MIN_LENGTH}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
          </div>
          <div className="field">
            <label htmlFor="repeat-password">{t('invitation.repeat')}</label>
            <input
              id="repeat-password"
              className="input"
              type="password"
              autoComplete="new-password"
              value={repeated}
              required
              minLength={MIN_LENGTH}
              onChange={(event) => {
                setRepeated(event.target.value);
              }}
            />
            <span className="muted" data-testid="invitation-policy">
              {t('password.policy', { min: MIN_LENGTH })}
            </span>
          </div>
          {error !== null ? (
            <div className="alert error" role="alert" data-testid="invitation-error">
              {error}
            </div>
          ) : null}
          <button type="submit" className="btn block" disabled={busy}>
            {busy ? t('invitation.submitting') : t('invitation.submit')}
          </button>
        </form>
      )}
    </div>
  );
}
